/**
 * VertexHandles — draggable bend-point handles for a selected EDG edge
 * (phase 6c, step 2).
 *
 * Port of the Rappid `marker-vertices` affordance the old EDG inherited from the
 * default `joint.dia.LinkView` (factories.js:createPaper used a plain LinkView
 * whose vertex handles are the standard JointJS ones). The baseline contract
 * (06-interactions.md §10 + 02-edg-editor.md "Use Shift+Click to add a vertex")
 * pins these behaviours:
 *   - a SELECTED edge shows a small circular handle at each persisted vertex,
 *   - dragging a handle moves that vertex (live, with the path following),
 *   - double-clicking a handle removes that vertex,
 *   - clicking a handle selects it; Delete / Backspace then removes it (the
 *     click must not reach the edge, or Delete hits the whole edge),
 *   - shift+click / double-click on the PATH inserts a new vertex (handled
 *     upstream in EdgFlow, ported from the custom LinkView.pointerclick/dblclick).
 *
 * The handles are HTML divs rendered through React Flow's `EdgeLabelRenderer`
 * (the same overlay the cardinality labels use), positioned in FLOW coordinates
 * via the viewport transform. While a handle is dragged, the new point is
 * reported up through `onVertexDragLive` so EdgFlow can re-derive THIS edge's
 * path from the live vertices (the path then follows the cursor). The MST commit
 * happens once on pointer-up through the existing vertex command — never mid-drag
 * (the commit-on-drag-end pattern; avoids feedback loops + history spam). Because
 * the live array flows back down as the `vertices` prop during the drag, the
 * handle and the path share a single source of truth and never diverge.
 *
 * Styling matches the Rappid vertex handle from the baseline screenshots: a small
 * white circle with the orange selection stroke (see edgFlow.scss
 * `.merode-vertex-handle`).
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { useReactFlow } from '@xyflow/react';
import type { Pt } from './edgeGeometry';
import { EDIT_BEND_POINT_HINT, isVertexDrag, moveVertex, removeVertex } from './vertexEditing';

export type VertexDragLive = (
  edgeId: string,
  kind: 'dependency' | 'inheritance',
  vertices: Pt[] | null
) => void;

export type VertexHandlesProps = {
  /** The edge id (object id of the dependency / inheritance). */
  edgeId: string;
  /** Which uimodel link store backs this edge — routes the vertex commit. */
  kind: 'dependency' | 'inheritance';
  /**
   * The vertices to render handles for. During a drag this is the LIVE array
   * (EdgFlow overrides it from `onVertexDragLive`), otherwise the persisted one.
   */
  vertices: Pt[];
  /** Commit a new vertices array (the existing updateEDG*Vertices command). */
  onChangeVertices: (edgeId: string, kind: 'dependency' | 'inheritance', vertices: Pt[]) => void;
  /**
   * Report the live vertices during a drag (or null on drag end / no drag) so the
   * edge path can follow the handle. EdgFlow stores this transiently and feeds it
   * back as the `vertices` prop until the commit lands.
   */
  onVertexDragLive: VertexDragLive;
  selectedIndex?: number | null;
  onSelectVertex?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
  onDeleteVertex?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
};

const HANDLE_HIT_RADIUS = 22;
const HANDLE_DOT_RADIUS = 5;

/**
 * Draggable handles for the persisted vertices of one edge. Rendered only when
 * the edge is selected (the caller gates on `selected`).
 */
export default function VertexHandles({
  edgeId,
  kind,
  vertices,
  onChangeVertices,
  onVertexDragLive,
  selectedIndex = null,
  onSelectVertex,
  onDeleteVertex,
}: VertexHandlesProps) {
  const { screenToFlowPosition } = useReactFlow();

  // Keep refs to the latest props so the window-level pointer listeners (bound
  // once per drag) read fresh values without re-binding.
  const verticesRef = React.useRef(vertices);
  verticesRef.current = vertices;
  const liveRef = React.useRef(onVertexDragLive);
  liveRef.current = onVertexDragLive;
  const commitRef = React.useRef(onChangeVertices);
  commitRef.current = onChangeVertices;
  const screenToFlowRef = React.useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;

  const handlePointerDown = React.useCallback(
    (evt: React.PointerEvent, index: number) => {
      // Only the primary button drags a vertex; let other buttons through.
      if (evt.button !== 0) return;
      // Stop React Flow from treating this as a pane/edge interaction.
      evt.stopPropagation();
      evt.preventDefault();
      onSelectVertex?.(edgeId, kind, index);

      // Snapshot the vertices at drag start so live updates only move `index`.
      const base = verticesRef.current.map((v) => ({ x: v.x, y: v.y }));

      // A click (no real travel) must not move the bend: see isVertexDrag.
      const start = { x: evt.clientX, y: evt.clientY };
      let dragging = false;

      const onMove = (moveEvt: PointerEvent) => {
        if (!dragging && !isVertexDrag(start, { x: moveEvt.clientX, y: moveEvt.clientY })) return;
        dragging = true;
        const flow = screenToFlowRef.current({ x: moveEvt.clientX, y: moveEvt.clientY });
        const next = moveVertex(base, index, flow);
        liveRef.current(edgeId, kind, next);
      };

      const onUp = (upEvt: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!dragging) return;
        const flow = screenToFlowRef.current({ x: upEvt.clientX, y: upEvt.clientY });
        const point = {
          x: Math.round(flow.x),
          y: Math.round(flow.y),
        };
        const next = moveVertex(base, index, point);
        // Clear the live override BEFORE committing so the re-derived path uses
        // the committed (persisted) vertices, not a stale transient array.
        liveRef.current(edgeId, kind, null);
        if (base[index].x !== point.x || base[index].y !== point.y) {
          commitRef.current(edgeId, kind, next);
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [edgeId, kind, onSelectVertex]
  );

  // Double-click on a handle removes that vertex (the Rappid default LinkView
  // vertex-removal gesture). The custom pointerdblclick only ADDS a vertex when
  // the target carries the connection class; a vertex handle does not, so the
  // default removal applies — reproduced here explicitly.
  const handleDoubleClick = React.useCallback(
    (evt: React.MouseEvent, index: number) => {
      evt.stopPropagation();
      evt.preventDefault();
      if (onDeleteVertex) {
        onDeleteVertex(edgeId, kind, index);
      } else {
        onChangeVertices(edgeId, kind, removeVertex(verticesRef.current, index));
      }
    },
    [onChangeVertices, onDeleteVertex, edgeId, kind]
  );

  if (vertices.length === 0) return null;

  return (
    <g className="merode-vertex-handles">
      {vertices.map((vertex, index) => (
        <g
          key={`${edgeId}-v${index}`}
          className={[
            'merode-vertex-handle',
            selectedIndex === index ? 'is-selected' : '',
            'nodrag',
            'nopan',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label="Bend point"
          onPointerDown={(evt) => handlePointerDown(evt, index)}
          // Keep the click off React Flow's edge click handler: it clears the
          // vertex selection, so Delete would then delete the whole edge.
          onClick={(evt) => evt.stopPropagation()}
          onDoubleClick={(evt) => handleDoubleClick(evt, index)}
        >
          <title>{EDIT_BEND_POINT_HINT}</title>
          <circle
            className="merode-vertex-handle-hit"
            cx={vertex.x}
            cy={vertex.y}
            r={HANDLE_HIT_RADIUS}
          />
          <circle
            className="merode-vertex-handle-dot"
            cx={vertex.x}
            cy={vertex.y}
            r={HANDLE_DOT_RADIUS}
          />
        </g>
      ))}
    </g>
  );
}
